import * as Y from 'yjs';
import { generateKeyBetween } from 'fractional-indexing';
import { OBJECT_PROPS_KEY, OBJECT_TEXT_KEY, RICH_TEXT_TYPES, Y_KEYS, type ObjectType } from './constants.js';
import { objectId as newObjectId } from './ids.js';
import {
  DEFAULT_SIZES,
  boardObjectSchema,
  createObjectInputSchema,
  type BoardObject,
  type CreateObjectInput,
} from './objects.js';
import { plainTextToDoc, type RichBlock, type RichDoc, type RichInline, type RichMark } from './richtext.js';
import { boardMetaSchema, type BoardMeta } from './board.js';

/**
 * Структура Y.Doc доски:
 *   objects: Y.Map<id, Y.Map<field, value>>
 *     поля объекта — плоские значения, `props` — вложенная Y.Map, `text` — Y.XmlFragment
 *   meta:     Y.Map — настройки доски
 *   comments: Y.Map<threadId, {x, y, objectId, resolved}>
 *   session:  Y.Map — голосование, таймер, презентация
 */

export type YObject = Y.Map<unknown>;

export const getObjectsMap = (doc: Y.Doc) => doc.getMap<YObject>(Y_KEYS.objects);
export const getMetaMap = (doc: Y.Doc) => doc.getMap<unknown>(Y_KEYS.meta);
export const getCommentsMap = (doc: Y.Doc) => doc.getMap<unknown>(Y_KEYS.comments);
export const getSessionMap = (doc: Y.Doc) => doc.getMap<unknown>(Y_KEYS.session);

const BASE_FIELDS = ['id', 'type', 'x', 'y', 'w', 'h', 'rotation', 'index', 'parentId', 'locked', 'createdBy', 'createdAt', 'updatedAt'] as const;

// ─── Rich text <-> Y.XmlFragment (совместимо с y-prosemirror) ─────────

function marksToAttrs(marks: RichMark[] | undefined): Record<string, unknown> | undefined {
  if (!marks || marks.length === 0) return undefined;
  const out: Record<string, unknown> = {};
  for (const m of marks) out[m.type] = m.attrs ?? {};
  return out;
}

function attrsToMarks(attrs: Record<string, unknown> | undefined): RichMark[] | undefined {
  if (!attrs) return undefined;
  const marks: RichMark[] = [];
  for (const [key, value] of Object.entries(attrs)) {
    // y-prosemirror добавляет хэш для перекрывающихся одинаковых марок: `link--abc`
    const type = key.replace(/--[a-zA-Z0-9+/=]{8}$/, '') as RichMark['type'];
    const a = value && typeof value === 'object' && Object.keys(value).length > 0 ? (value as Record<string, unknown>) : undefined;
    marks.push(a ? { type, attrs: a } : { type });
  }
  return marks.length ? marks : undefined;
}

function isInline(node: RichBlock | RichInline): node is RichInline {
  return node.type === 'text' || node.type === 'hardBreak';
}

type Delta = Array<{ insert: string; attributes?: Record<string, unknown> }>;

/** Строит дерево Y-узлов до интеграции в документ (Yjs поддерживает prelim-типы). */
function buildChildren(content: Array<RichBlock | RichInline>): Array<Y.XmlElement | Y.XmlText> {
  const items: Array<Y.XmlElement | Y.XmlText> = [];
  let text: Y.XmlText | null = null;
  let delta: Delta = [];
  const flush = () => {
    if (text) text.applyDelta(delta);
    text = null;
    delta = [];
  };
  for (const node of content) {
    if (node.type === 'text') {
      if (!text) {
        text = new Y.XmlText();
        items.push(text);
      }
      const attributes = marksToAttrs(node.marks);
      delta.push(attributes ? { insert: node.text ?? '', attributes } : { insert: node.text ?? '' });
      continue;
    }
    flush();
    const el = new Y.XmlElement(node.type);
    if (!isInline(node)) {
      for (const [k, v] of Object.entries(node.attrs ?? {})) {
        if (v !== undefined && v !== null) el.setAttribute(k, v as string);
      }
      if (node.content?.length) el.insert(0, buildChildren(node.content));
    }
    items.push(el);
  }
  flush();
  return items;
}

/** Записывает документ в пустой (или очищаемый) фрагмент. Должно вызываться внутри транзакции. */
export function writeRichDoc(fragment: Y.XmlFragment, doc: RichDoc): void {
  if (fragment.length) fragment.delete(0, fragment.length);
  fragment.insert(0, buildChildren(doc.content));
}

function readNode(node: Y.XmlElement | Y.XmlText): Array<RichBlock | RichInline> {
  if (node instanceof Y.XmlText) {
    const delta = node.toDelta() as Array<{ insert: unknown; attributes?: Record<string, unknown> }>;
    return delta
      .filter((d) => typeof d.insert === 'string')
      .map((d) => {
        const marks = attrsToMarks(d.attributes);
        return marks ? { type: 'text' as const, text: d.insert as string, marks } : { type: 'text' as const, text: d.insert as string };
      });
  }
  const type = node.nodeName as RichBlock['type'] | 'hardBreak';
  if (type === 'hardBreak') return [{ type: 'hardBreak' }];
  const attrs = node.getAttributes() as Record<string, unknown>;
  const content: Array<RichBlock | RichInline> = [];
  for (const child of node.toArray()) {
    if (child instanceof Y.XmlElement || child instanceof Y.XmlText) content.push(...readNode(child));
  }
  const block: RichBlock = { type };
  if (Object.keys(attrs).length) block.attrs = attrs;
  if (content.length) block.content = content;
  return [block];
}

export function readRichDoc(fragment: Y.XmlFragment | null | undefined): RichDoc {
  const content: RichBlock[] = [];
  if (fragment) {
    for (const child of fragment.toArray()) {
      if (child instanceof Y.XmlElement || child instanceof Y.XmlText) {
        for (const n of readNode(child)) {
          if (!isInline(n)) content.push(n);
          else content.push({ type: 'paragraph', content: [n] });
        }
      }
    }
  }
  return { type: 'doc', content: content.length ? content : [{ type: 'paragraph' }] };
}

// ─── Объекты ─────────────────────────────────────────────────────────

/** Создаёт Y.Map объекта. Текст пишется после вставки в документ — см. `insertObject`. */
function buildYObject(obj: BoardObject): YObject {
  const m = new Y.Map<unknown>();
  for (const key of BASE_FIELDS) m.set(key, (obj as Record<string, unknown>)[key] ?? null);
  const props = new Y.Map<unknown>();
  for (const [k, v] of Object.entries(obj.props)) props.set(k, v);
  m.set(OBJECT_PROPS_KEY, props);
  if (RICH_TEXT_TYPES.has(obj.type)) m.set(OBJECT_TEXT_KEY, new Y.XmlFragment());
  return m;
}

/** Вставляет объект в документ (внутри транзакции вызывающей стороны или своей). */
export function insertObject(doc: Y.Doc, obj: BoardObject, origin?: unknown): void {
  const objects = getObjectsMap(doc);
  doc.transact(() => {
    const m = buildYObject(obj);
    objects.set(obj.id, m);
    if (obj.text && RICH_TEXT_TYPES.has(obj.type)) {
      const frag = m.get(OBJECT_TEXT_KEY) as Y.XmlFragment;
      writeRichDoc(frag, obj.text);
    }
  }, origin);
}

/** Плоский объект из Y.Map (без текста). */
export function readObject(m: YObject): BoardObject | null {
  const raw: Record<string, unknown> = {};
  for (const key of BASE_FIELDS) raw[key] = m.get(key);
  const props = m.get(OBJECT_PROPS_KEY);
  raw.props = props instanceof Y.Map ? props.toJSON() : (props ?? {});
  if (typeof raw.type !== 'string') return null;
  return raw as unknown as BoardObject;
}

/** Объект вместе с rich text (для экспорта, API, буфера обмена). */
export function readObjectWithText(m: YObject): BoardObject | null {
  const obj = readObject(m);
  if (!obj) return null;
  const frag = m.get(OBJECT_TEXT_KEY);
  if (frag instanceof Y.XmlFragment) return { ...obj, text: readRichDoc(frag) };
  return obj;
}

export function getTextFragment(m: YObject): Y.XmlFragment | null {
  const frag = m.get(OBJECT_TEXT_KEY);
  return frag instanceof Y.XmlFragment ? frag : null;
}

export interface ObjectPatch {
  x?: number;
  y?: number;
  w?: number;
  h?: number;
  rotation?: number;
  index?: string;
  parentId?: string | null;
  locked?: boolean;
  props?: Record<string, unknown>;
  text?: RichDoc;
}

/** Применяет частичное изменение к Y.Map объекта (внутри транзакции). */
export function patchYObject(m: YObject, patch: ObjectPatch, now = Date.now()): void {
  let changed = false;
  for (const key of ['x', 'y', 'w', 'h', 'rotation', 'index', 'parentId', 'locked'] as const) {
    const v = patch[key];
    if (v !== undefined && m.get(key) !== v) {
      m.set(key, v);
      changed = true;
    }
  }
  if (patch.props) {
    let props = m.get(OBJECT_PROPS_KEY);
    if (!(props instanceof Y.Map)) {
      props = new Y.Map<unknown>();
      m.set(OBJECT_PROPS_KEY, props);
    }
    const pm = props as Y.Map<unknown>;
    for (const [k, v] of Object.entries(patch.props)) {
      if (v === undefined) continue;
      const cur = pm.get(k);
      if (cur === v) continue;
      if (typeof v === 'object' && v !== null && JSON.stringify(cur) === JSON.stringify(v)) continue;
      pm.set(k, v);
      changed = true;
    }
  }
  if (patch.text) {
    let frag = m.get(OBJECT_TEXT_KEY);
    if (!(frag instanceof Y.XmlFragment)) {
      frag = new Y.XmlFragment();
      m.set(OBJECT_TEXT_KEY, frag);
    }
    writeRichDoc(frag as Y.XmlFragment, patch.text);
    changed = true;
  }
  if (changed) m.set('updatedAt', now);
}

/** Самый верхний z-index среди объектов документа. */
export function topIndex(doc: Y.Doc): string | null {
  let max: string | null = null;
  getObjectsMap(doc).forEach((m) => {
    const idx = m.get('index');
    if (typeof idx === 'string' && (max === null || idx > max)) max = idx;
  });
  return max;
}

export function indexAbove(current: string | null): string {
  return generateKeyBetween(current, null);
}

/**
 * Нормализует вход API/импорта в полноценный объект доски:
 * подставляет id, размеры по умолчанию, z-index, даты, валидирует zod-схемой.
 */
export function normalizeObjectInput(
  input: CreateObjectInput,
  ctx: { userId: string | null; index?: string; now?: number },
): BoardObject {
  const parsed = createObjectInputSchema.parse(input);
  const now = ctx.now ?? Date.now();
  const def = DEFAULT_SIZES[parsed.type as ObjectType];
  const text = typeof parsed.text === 'string' ? plainTextToDoc(parsed.text) : parsed.text;
  const candidate = {
    id: parsed.id ?? newObjectId(),
    type: parsed.type,
    x: parsed.x,
    y: parsed.y,
    w: parsed.w ?? def.w,
    h: parsed.h ?? def.h,
    rotation: parsed.rotation,
    index: parsed.index ?? ctx.index ?? 'a0',
    parentId: parsed.parentId,
    locked: parsed.locked,
    createdBy: ctx.userId,
    createdAt: now,
    updatedAt: now,
    props: parsed.props,
    ...(text && RICH_TEXT_TYPES.has(parsed.type as ObjectType) ? { text } : {}),
  };
  return boardObjectSchema.parse(candidate);
}

export function readMeta(doc: Y.Doc): BoardMeta {
  return boardMetaSchema.parse(getMetaMap(doc).toJSON());
}

/** Все объекты документа с текстом (экспорт). */
export function readAllObjects(doc: Y.Doc, withText = true): BoardObject[] {
  const out: BoardObject[] = [];
  getObjectsMap(doc).forEach((m) => {
    const o = withText ? readObjectWithText(m) : readObject(m);
    if (o) out.push(o);
  });
  out.sort((a, b) => (a.index < b.index ? -1 : a.index > b.index ? 1 : 0));
  return out;
}
